/**
 * BHIMA POS Pay Later — dues list, customer lookup, settle.
 * Charge itself remains POST /store/pos/sales with payment_method PAY_LATER.
 */
import { apiFetch } from './client';
import { phoneDigits } from '../utils/posMoney';
import { roundMoney } from '../utils/soldBy';

export const PAY_LATER_STATUS = {
  PENDING: 'PENDING',
  COLLECTED: 'COLLECTED',
};

/** @deprecated Use COLLECTED — kept for older UI labels. */
PAY_LATER_STATUS.PAID = PAY_LATER_STATUS.COLLECTED;

export const PAY_LATER_STATUS_LABELS = {
  [PAY_LATER_STATUS.PENDING]: 'Pending',
  [PAY_LATER_STATUS.COLLECTED]: 'Paid',
};

export const ADDRESS_LINE2_OPTIONS = [
  'Jaitra',
  'SMR',
  'Gokul Plots',
  'Other',
];

export const ADDRESS_LINE2_OTHER = 'Other';

export const PAY_LATER_SETTLE_METHODS = [
  { key: 'CASH', label: 'Cash' },
  { key: 'UPI', label: 'UPI' },
  { key: 'CARD', label: 'Card' },
];

export const PAY_LATER_ERRORS = {
  not_found: 'Pay later bill not found.',
  already_settled: 'Already collected — refresh the list.',
  already_paid: 'Already collected — refresh the list.',
  already_voided: 'This bill was voided — refresh the list.',
  not_pay_later: 'This is not a Pay Later bill.',
  nothing_due: 'Nothing left to collect on this bill.',
  invalid_payment_method: 'Choose Cash, UPI, or Card.',
  invalid_status: 'Only pending pay laters can be marked paid.',
  missing_customer: 'Customer details are required.',
  customer_name_required: 'Enter the customer’s full name.',
  customer_phone_required: 'Enter a 10-digit phone number.',
  invalid_phone: 'Enter a valid 10-digit phone number.',
  address_required: 'Enter Address Line 1 or select a society (Address Line 2).',
  address_line1_required: 'Enter Address Line 1.',
  address_line2_required: 'Enter or select Address Line 2.',
  invalid_address_line1: 'Address Line 1 is invalid.',
  invalid_address_line2: 'Address Line 2 is invalid.',
  block_required: 'Enter the block (required with society).',
  flat_required: 'Enter the flat (required with society).',
  invalid_paid_now: 'Enter an amount greater than zero and less than the bill total.',
  invalid_paid_now_method: 'Choose Cash, UPI, or Card for the amount paid now.',
  paid_now_covers_total: 'That covers the whole bill — charge Cash, UPI, or Card instead.',
  invalid_settle_amount: 'Enter an amount greater than zero.',
  amount_exceeds_outstanding: 'That is more than the amount still due.',
  settle_conflict: 'Someone else updated this bill — refresh and try again.',
  pay_later_has_collections: 'This bill already has payments. Collect the rest here. Void is available after it is fully paid.',
  amend_below_collected: 'New total cannot be less than the amount already collected.',
  forbidden: 'You do not have permission to manage Pay Later.',
};

export function newClientPaymentId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : ((random & 0x3) | 0x8);
    return value.toString(16);
  });
}

function normalizeAddress(raw = {}) {
  return {
    customerName: raw.customer_name || raw.customerName || '',
    addressLine1: raw.address_line1 || raw.addressLine1 || '',
    addressLine2: raw.address_line2 || raw.addressLine2 || '',
    block: raw.block || '',
    flat: raw.flat || '',
    lastUsedAt: raw.last_used_at || raw.lastUsedAt || null,
  };
}

export function normalizePayLaterItem(raw = {}) {
  const paymentStatus = String(raw.payment_status || raw.paymentStatus || '').toUpperCase()
    || PAY_LATER_STATUS.PENDING;
  const status = paymentStatus === PAY_LATER_STATUS.COLLECTED || paymentStatus === 'PAID'
    ? PAY_LATER_STATUS.COLLECTED
    : PAY_LATER_STATUS.PENDING;
  const phone = phoneDigits(raw.customer_phone || raw.customerPhone || '');
  const payments = Array.isArray(raw.payments) ? raw.payments : [];
  const summedPaid = roundMoney(payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0));
  const amountPaid = raw.amount_paid != null || raw.amountPaid != null
    ? roundMoney(raw.amount_paid ?? raw.amountPaid)
    : summedPaid;
  const total = roundMoney(raw.total);
  const outstanding = status === PAY_LATER_STATUS.COLLECTED
    ? roundMoney(raw.outstanding ?? 0)
    : roundMoney(raw.outstanding != null ? raw.outstanding : Math.max(0, total - amountPaid));
  return {
    id: raw.order_id || raw.orderId || raw.id,
    orderId: raw.order_id || raw.orderId || raw.id,
    displayId: raw.display_id || raw.displayId || raw.order_id || raw.id,
    createdAt: raw.created_at || raw.createdAt || null,
    amount: status === PAY_LATER_STATUS.COLLECTED ? total : outstanding,
    total,
    amountPaid,
    outstanding,
    payments,
    paidByMethod: raw.paid_by_method || raw.paidByMethod || null,
    status,
    paymentStatus: status,
    paymentMethod: raw.payment_method || 'PAY_LATER',
    counter: raw.counter || null,
    cashierSub: raw.cashier_sub || raw.cashierSub || null,
    customer: {
      fullName: raw.customer_name || raw.customerName || '',
      phone,
      addressLine1: raw.address_line1 || raw.addressLine1 || '',
      addressLine2: raw.address_line2 || raw.addressLine2 || '',
      block: raw.block || '',
      flat: raw.flat || '',
    },
    paidAt: raw.settled_at || raw.settledAt || null,
    paidVia: raw.settlement_method || raw.settlementMethod || null,
    settlementAmount: raw.settlement_amount != null ? Number(raw.settlement_amount) : null,
    settlementRoundOff: raw.settlement_round_off != null ? Number(raw.settlement_round_off) : null,
    settledBy: raw.settled_by || raw.settledBy || null,
    note: raw.note || null,
  };
}

/**
 * Lookup prior Pay Later snapshots for a phone (after 10 digits).
 * @returns {{ phone: string, customerName: string|null, balance: number, openCount: number, creditLimit: null, addresses: object[] }}
 */
export async function lookupPayLaterCustomer(phone, token) {
  const digits = phoneDigits(phone);
  if (digits.length !== 10) {
    return {
      phone: '',
      customerName: null,
      balance: 0,
      openCount: 0,
      creditLimit: null,
      addresses: [],
    };
  }
  const raw = await apiFetch(
    `/store/pos/pay-later/customers?phone=${encodeURIComponent(digits)}`,
    { token },
  );
  return {
    phone: raw.phone || `+91${digits}`,
    customerName: raw.customer_name ?? raw.customerName ?? null,
    balance: Number(raw.balance) || 0,
    openCount: Number(raw.open_count ?? raw.openCount) || 0,
    creditLimit: raw.credit_limit ?? raw.creditLimit ?? null,
    addresses: Array.isArray(raw.addresses) ? raw.addresses.map(normalizeAddress) : [],
  };
}

/**
 * @param {{ status?: 'PENDING'|'COLLECTED'|'ALL'|'pending'|'collected'|'all', q?: string, limit?: number, cursor?: string }} [filters]
 */
export async function listPayLaters(filters = {}, token) {
  const statusRaw = String(filters.status || 'pending').toLowerCase();
  const status = statusRaw === 'all'
    ? 'all'
    : (statusRaw === 'collected' || statusRaw === 'paid' ? 'collected' : 'pending');
  const params = new URLSearchParams();
  params.set('status', status);
  if (filters.q) params.set('q', String(filters.q).trim());
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.cursor) params.set('cursor', String(filters.cursor));
  const raw = await apiFetch(`/store/pos/pay-later?${params.toString()}`, { token });
  const items = Array.isArray(raw.items) ? raw.items.map(normalizePayLaterItem) : [];
  const summary = raw.summary || {};
  return {
    items,
    status: raw.status || status,
    summary: {
      openBalance: Number(summary.open_balance ?? summary.openBalance) || 0,
      openCount: Number(summary.open_count ?? summary.openCount) || 0,
      creditLimit: summary.credit_limit ?? summary.creditLimit ?? null,
    },
    nextCursor: raw.next_cursor || raw.nextCursor || null,
  };
}

export async function countPendingPayLaters(token) {
  if (!token) return 0;
  try {
    const { summary } = await listPayLaters({ status: 'pending', limit: 1 }, token);
    return summary.openCount || 0;
  } catch {
    return 0;
  }
}

/**
 * Collect part or all of a Pay Later bill.
 * Omit amount (or send the full outstanding) to pay the rest.
 * @param {string} orderId
 * @param {{
 *   payment_method: 'CASH'|'UPI'|'CARD',
 *   amount?: number,
 *   note?: string,
 *   client_payment_id?: string,
 *   client_settle_id?: string,
 *   settled_at?: string,
 * }} payload
 */
export async function settlePayLater(orderId, payload = {}, token) {
  const method = String(payload.payment_method || '').toUpperCase();
  if (!['CASH', 'UPI', 'CARD'].includes(method)) {
    const err = new Error(PAY_LATER_ERRORS.invalid_payment_method);
    err.code = 'invalid_payment_method';
    throw err;
  }
  const body = { payment_method: method };
  if (payload.amount != null && payload.amount !== '') {
    const amount = roundMoney(payload.amount);
    if (!(amount > 0)) {
      const err = new Error(PAY_LATER_ERRORS.invalid_settle_amount);
      err.code = 'invalid_settle_amount';
      throw err;
    }
    body.amount = amount;
  }
  const note = String(payload.note || '').trim();
  if (note) body.note = note.slice(0, 200);
  const paymentId = payload.client_payment_id || payload.client_settle_id;
  if (paymentId) {
    body.client_payment_id = String(paymentId);
    body.settled_at = payload.settled_at || new Date().toISOString();
  }
  return apiFetch(`/store/pos/sales/${encodeURIComponent(orderId)}/settle`, {
    method: 'POST',
    token,
    body,
  });
}

/** @deprecated Use settlePayLater */
export async function markPayLaterPaid(id, payload = {}, token) {
  return settlePayLater(id, payload, token);
}

export function formatPayLaterAddress(customer) {
  if (!customer) return '—';
  const parts = [
    customer.addressLine1,
    customer.addressLine2,
    customer.block ? `Block ${customer.block}` : null,
    customer.flat ? `Flat ${customer.flat}` : null,
  ].filter(Boolean);
  return parts.join(', ') || '—';
}

export function mapPayLaterError(err) {
  const code = err?.code || err?.message;
  return PAY_LATER_ERRORS[code] || err?.detail || err?.message || 'Something went wrong.';
}
